import classnames from 'classnames';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { ConfirmDialogueModal } from '../../../../../../components/modals/confirm-dialogue-modal';
import { TRANSFER_ORDER_ACTION_STATUS_CHANGED } from '../../../../../../constants/analytics-events';
import { useModal } from '../../../../../../hooks/use-modal';
import { useSendMetrics } from '../../../../../../hooks/use-send-metrics';
import { patchTransferOrderStatus } from '../../../api/transfer-order-api';
import { TransferOrderStatus } from '../../../components/transfer-order-status/transfer-order-status';
import {
  canCancelTransferOrder,
  canCloseTransferOrder,
  canEditTransferOrder,
  canReceiveTransferOrder,
  hasAvailableActions,
} from '../../../data/hooks/useTransferOrderPermissions';
import { transferOrderStatuses } from '../../../data/transfer-order-statuses';
import type { TransferOrderRow } from '../../../data/transfer-orders-context.types';
import { ReceiveTransferOrderButton } from '../../../receive-transfer-order-drawer/receive-transfer-order-button';
import { TransferOrderConfirmCancelModal } from '../transfer-order-confirm-cancel-modal/transfer-order-confirm-cancel-modal';
import { messages } from './transfer-order-header.messages';
import styles from './transfer-order-header.styles.module.less';

type TransferOrderHeaderProps = {
  transferOrder: TransferOrderRow;
  isEditMode: boolean;
  hasChanged: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancelEdit: () => void;
  onActionSuccess: () => void;
  onFetchTransferOrder: () => void;
};

export const TransferOrderHeader = ({
  transferOrder,
  isEditMode,
  hasChanged,
  isSaving,
  onEdit,
  onSave,
  onCancelEdit,
  onActionSuccess,
  onFetchTransferOrder,
}: TransferOrderHeaderProps) => {
  const { status, transferOrderId, orderNumber } = transferOrder;
  const [isLoading, setIsLoading] = useState(false);
  const { open: openCancelEditModal, close: closeCancelEditModal, isOpen: isCancelEditModalOpen } = useModal();
  const { open: openConfirmCancelModal, close: closeConfirmCancelModal, isOpen: isConfirmCancelModalOpen } = useModal();
  const { open: openConfirmCloseModal, close: closeConfirmCloseModal, isOpen: isConfirmCloseModalOpen } = useModal();
  const trackMetrics = useSendMetrics();

  const handleCancelEdit = useCallback(() => {
    if (hasChanged) {
      openCancelEditModal();
      return;
    }
    onCancelEdit();
  }, [hasChanged, openCancelEditModal, onCancelEdit]);

  const handleConfirmDiscard = useCallback(() => {
    closeCancelEditModal();
    onCancelEdit();
  }, [closeCancelEditModal, onCancelEdit]);

  const handleCancelOrder = async (restoreInventory: boolean) => {
    setIsLoading(true);
    try {
      await patchTransferOrderStatus(transferOrderId, transferOrderStatuses.cancelled, restoreInventory);
      trackMetrics(TRANSFER_ORDER_ACTION_STATUS_CHANGED, { transferOrderId, newStatus: transferOrderStatuses.cancelled });
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.cancelSuccessMessage} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
      closeConfirmCancelModal();
      onActionSuccess();
    } catch {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.cancelErrorMessage} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseOrder = async () => {
    setIsLoading(true);
    try {
      await patchTransferOrderStatus(transferOrderId, transferOrderStatuses.closed);
      trackMetrics(TRANSFER_ORDER_ACTION_STATUS_CHANGED, { transferOrderId, newStatus: transferOrderStatuses.closed });
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.closeSuccessMessage} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
      closeConfirmCloseModal();
      onActionSuccess();
    } catch {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.closeErrorMessage} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FlexRow className={styles.header}>
      <FlexRow className={styles['title-container']}>
        <Text
          className={styles.title}
          formattedMessageValues={{ orderId: orderNumber }}
          textType={TextType.H1}
          value={messages.orderNumberHeader}
        />
        <div className={styles['status-wrapper']}>
          <TransferOrderStatus status={status} />
        </div>
      </FlexRow>
      <FlexRow className={styles['buttons-container']} gap={Gap.Space8}>
        {isEditMode && (
          <>
            <Button className={styles['cancel-button']} onClick={handleCancelEdit} theme={THEME_CONSTANTS.tertiary}>
              <Text textType={TextType.Label} value={messages.cancel} />
            </Button>
            <Button className={styles['save-button']} disabled={!hasChanged || isSaving} onClick={onSave} theme={THEME_CONSTANTS.primary}>
              <Text textType={TextType.Label} value={messages.save} />
            </Button>
          </>
        )}
        {isLoading && <LoadingIndicator />}
        {!isLoading && hasAvailableActions(status) && (
          <DropdownMenu
            className={classnames(styles['menu-dropdown'], { [styles['menu-dropdown-disabled']]: isEditMode })}
            disabled={isEditMode}
            menuPosition='bottom-left'
          >
            <DropdownMenuToggler
              className={classnames(styles['actions-button'], { [styles['actions-button-disabled']]: isEditMode })}
              disabled={isEditMode}
            >
              <Text className={styles['actions-text']} textType={TextType.Label} value={messages.actionsDropdown} />
            </DropdownMenuToggler>
            {canEditTransferOrder(status) && !isEditMode && (
              <DropdownMenuItem onClick={onEdit}>
                <Text textType={TextType.Paragraph} value={messages.editAction} />
              </DropdownMenuItem>
            )}
            {canCancelTransferOrder(status) && (
              <DropdownMenuItem onClick={openConfirmCancelModal}>
                <Text textType={TextType.Paragraph} value={messages.actionsCancelOrder} />
              </DropdownMenuItem>
            )}
          </DropdownMenu>
        )}
        {canReceiveTransferOrder(status) && (
          <ReceiveTransferOrderButton
            buttonMessage={messages.receiveStock}
            disabled={!canReceiveTransferOrder(status) || isEditMode}
            onSuccess={onFetchTransferOrder}
            theme={THEME_CONSTANTS.primary}
            transferOrder={transferOrder}
          />
        )}
        {canCloseTransferOrder(status) && (
          <Button onClick={openConfirmCloseModal} theme={THEME_CONSTANTS.primary}>
            <Text textType={TextType.Label} value={messages.actionsCloseOrder} />
          </Button>
        )}
      </FlexRow>

      {isCancelEditModalOpen && (
        <ConfirmDialogueModal
          body={<FormattedMessage {...messages.confirmCancelBody} />}
          buttonMessage={<FormattedMessage {...messages.confirmDiscardButton} />}
          header={<FormattedMessage {...messages.confirmCancelHeader} />}
          onClose={closeCancelEditModal}
          onConfirm={handleConfirmDiscard}
        />
      )}

      {isConfirmCloseModalOpen && (
        <ConfirmDialogueModal
          body={<FormattedMessage {...messages.confirmCloseBody} values={{ orderNumber }} />}
          buttonMessage={<FormattedMessage {...messages.confirmCloseButton} />}
          header={<FormattedMessage {...messages.confirmCloseHeader} />}
          onClose={closeConfirmCloseModal}
          onConfirm={handleCloseOrder}
        />
      )}

      {isConfirmCancelModalOpen && (
        <TransferOrderConfirmCancelModal close={closeConfirmCancelModal} isLoading={isLoading} onConfirm={handleCancelOrder} />
      )}
    </FlexRow>
  );
};
